import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineError, Skeleton } from '../../components/ui';
import { hoursLabel, manilaWindow } from './analyzeHours';
import { analyzeState } from './analyzeState';
import { editsAfter } from './changeLog';
import { reportBlocks } from './reportText';
import type { SavedReport, ScoreV3Api } from './scoreboardV3Api';
import './scoreboardV3.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function longDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  return `${WEEKDAYS[d.getUTCDay()] ?? ''} ${String(d.getUTCDate())} ${MONTHS[d.getUTCMonth()] ?? ''}`;
}
function clock(iso: string): string {
  return new Date(iso)
    .toLocaleTimeString('en-US', { timeZone: 'Asia/Manila', hour: 'numeric', minute: '2-digit' })
    .replace(/\s/g, ' ');
}

export const REPORTS_KEY = ['marketing-score-reports'] as const;
/** The (i) text. The Manila hours move by one when New York changes its clocks. */
function tipText(now: Date): string {
  return `Analyze reads the marketing and sales numbers and writes a short report: what moved, where the funnel leaks, one action per owner. Usually ready in 2 to 4 minutes. Available ${hoursLabel(now)}.`;
}

/** The clock the card reads. Ticks every 20 seconds so the countdown and the ten minute rule stay true. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 20_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/** The (i) next to Analyze. Click toggles, hover opens on a mouse (CSS), Escape closes. */
function InfoTip({ now, status = [] }: { now: Date; status?: string[] }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (!(e.target as Element).closest('.score-info')) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);
  return (
    <span className="score-info">
      <button
        type="button"
        className="score-info-i"
        aria-label="About Analyze"
        aria-expanded={open}
        aria-controls="score-an-tip"
        onClick={() => setOpen((v) => !v)}
      />
      <span
        id="score-an-tip"
        role="tooltip"
        className={open ? 'score-an-tip score-an-tip--open' : 'score-an-tip'}
      >
        <span>{tipText(now)}</span>
        {status.map((line) => (
          <span key={line} className="score-an-tip-line">
            {line}
          </span>
        ))}
      </span>
    </span>
  );
}

/** The card at the top of the Board. The database decides who may run; this only shows the state. */
export function AnalyzeCard({ api, onRead }: { api: ScoreV3Api; onRead: () => void }) {
  const client = useQueryClient();
  const now = useNow();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const top = useQuery({
    queryKey: ['marketing-score-top-member'],
    queryFn: () => api.isTopMember(),
    retry: false,
  });
  const reports = useQuery({
    queryKey: REPORTS_KEY,
    queryFn: () => api.reports(),
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.some((r) => r.status === 'queued' || r.status === 'running') ? 5000 : false,
  });
  if (top.isPending || reports.isPending) return <Skeleton lines={3} label="Loading Analyze" />;
  if (top.isError || reports.isError)
    return <InlineError>The Analyze card could not load. Reload to try again.</InlineError>;
  const state = analyzeState({ isTopMember: top.data, reports: reports.data, now });
  const press = async () => {
    setBusy(true);
    setError('');
    try {
      await api.analyze();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The report could not be started.');
    } finally {
      setBusy(false);
      await client.invalidateQueries({ queryKey: REPORTS_KEY });
    }
  };
  const kind = state.kind;
  const reader = kind === 'used' || kind === 'denied';
  const inFlight = kind === 'running' || kind === 'waiting' || busy;
  const label = busy
    ? 'Analyzing…'
    : kind === 'running'
      ? 'Analyzing…'
      : kind === 'waiting'
        ? 'Still waiting…'
        : state.primary;
  const failedNote =
    state.by !== null && state.at !== null
      ? `The last try did not finish (${state.at} Manila)`
      : 'The last try did not finish';
  // Everything that used to sit on the card now lives behind the (i).
  const status: string[] =
    kind === 'ready'
      ? ['1 report left today', 'It is used only when the report is saved']
      : kind === 'running'
        ? [`${state.by ?? ''} started this at ${state.at ?? ''} Manila`, 'You can leave this page']
        : kind === 'waiting'
          ? [
              `Sent by ${state.by ?? ''} at ${state.at ?? ''} Manila, ${String(state.minutesWaiting)} minutes ago`,
              `Kyu\u2019s Mac has not picked it up yet. It retries every minute until 2 PM ET (${manilaWindow(now).closes} Manila). Nothing is used up until a report is saved.`,
            ]
          : kind === 'closed'
            ? ['Closed now', 'Nothing is queued. Today\u2019s report is still free.']
            : kind === 'used'
              ? [
                  'Today\u2019s report is done',
                  `Run by ${state.by ?? ''} at ${state.at ?? ''} Manila`,
                  `Next one opens 6 AM ET tomorrow (in ${state.resetIn ?? ''})`,
                ]
              : kind === 'failed'
                ? [failedNote, 'Nothing was saved, and you still have today\u2019s report.']
                : ['Only Kyu and the named top members can run the daily report'];
  return (
    <section className="score-analyze" aria-label="Analyze report">
      <div className="score-btn-row">
        {reader ? (
          <Button onClick={onRead}>{state.primary}</Button>
        ) : (
          <Button
            variant={kind === 'ready' || kind === 'failed' ? 'primary' : 'secondary'}
            disabled={!state.canPress || inFlight}
            onClick={() => void press()}
          >
            {label}
          </Button>
        )}
        <InfoTip now={now} status={status} />
      </div>
      {error ? <InlineError>{error}</InlineError> : null}
    </section>
  );
}

/** The reader and the list of past reports. A saved report is never rewritten. */
export function AnalysisTab({
  api,
  onChangeLog,
  onBoard,
}: {
  api: ScoreV3Api;
  onChangeLog: () => void;
  onBoard: () => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const reports = useQuery({ queryKey: REPORTS_KEY, queryFn: () => api.reports(), retry: false });
  const log = useQuery({
    queryKey: ['marketing-score-change-log'],
    queryFn: () => api.changeLog(),
    retry: false,
  });
  if (reports.isPending) return <Skeleton lines={6} label="Loading reports" />;
  if (reports.isError)
    return <InlineError>Reports could not load. Reload to try again.</InlineError>;
  const done = reports.data.filter(
    (r): r is SavedReport & { report: NonNullable<SavedReport['report']> } =>
      r.status === 'done' && r.report !== null,
  );
  if (done.length === 0)
    return (
      <section className="score-card score-empty-state">
        <h3>No reports yet</h3>
        <p className="score-muted">
          When someone presses <strong>Analyze now</strong> on the Board, the report is saved here
          so anyone on TAP can read it later.
        </p>
        <Button variant="secondary" onClick={onBoard}>
          Go to the Board
        </Button>
      </section>
    );
  const current = done.find((r) => r.id === picked) ?? done[0]!;
  const edited = log.data ? editsAfter(log.data, current.finished_at) : 0;
  const body = current.report;
  return (
    <div className="score-split">
      <section className="score-card" aria-label="Past reports">
        <header className="score-card-head">
          <div>
            <strong>Past reports</strong>
            <small>Newest first. One per day at most.</small>
          </div>
        </header>
        <ul className="score-list">
          {done.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className={
                  r.id === current.id ? 'score-list-item score-list-item--on' : 'score-list-item'
                }
                aria-pressed={r.id === current.id}
                onClick={() => setPicked(r.id)}
              >
                <strong>{longDay(r.report_day)}</strong>
                <span>{r.headline}</span>
                <small>
                  Run by {r.started_by_name}, {clock(r.started_at)} Manila
                </small>
              </button>
            </li>
          ))}
        </ul>
      </section>
      <article
        className="score-card score-reader"
        aria-label={`Report for ${longDay(current.report_day)}`}
      >
        <header>
          <small>Analyze report · {longDay(current.report_day)} · numbers for the day before</small>
          <h2>{body.headline}</h2>
          <small>
            Started by {current.started_by_name} at {clock(current.started_at)} Manila ·{' '}
            {current.model}
          </small>
        </header>
        {edited > 0 ? (
          <div className="score-callout score-callout--warn" role="note">
            <strong>
              {edited} {edited === 1 ? 'number was' : 'numbers were'} edited after this report ran.
            </strong>{' '}
            The report below shows what it saw at {clock(current.finished_at ?? current.started_at)}
            .{' '}
            <Button variant="ghost" onClick={onChangeLog}>
              See what changed
            </Button>
          </div>
        ) : null}
        <div className="score-report-text">
          {reportBlocks(body.text).map((b, i) =>
            b.kind === 'heading' ? (
              <section key={i}>
                <h3>{b.text}</h3>
                {b.rest ? <p>{b.rest}</p> : null}
              </section>
            ) : b.kind === 'bullet' ? (
              <ul key={i}>
                <li>{b.text}</li>
              </ul>
            ) : (
              <p key={i}>{b.text}</p>
            ),
          )}
        </div>
      </article>
    </div>
  );
}
